import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, XCircle } from 'lucide-react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar/sidebar';
import api from '../../utils/api';
import { isAuthenticated } from '../../utils/auth';
import '../../components/Header.css';
import './NotificationHistory.css';
import { API_BASE_URL } from '../../utils/apiBase';

const DISMISSED_NOTIFICATIONS_KEY = 'dismissedNotificationIds';

const getDismissedNotificationIds = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const NotificationHistory = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(getDismissedNotificationIds());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeRequest, setActiveRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`${API_BASE_URL}/api/notifications/history`);
        if (active) {
          setNotifications(res.data || []);
          setDismissedIds(getDismissedNotificationIds());
        }
      } catch (err) {
        if (active) {
          setError('Failed to load notifications.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      active = false;
    };
  }, []);

  const visibleNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const isDismissed = dismissedIds.includes(notification._id);
      if (filter === 'read') {
        return notification.isRead && !isDismissed;
      }
      if (filter === 'unread') {
        return !notification.isRead && !isDismissed;
      }
      if (filter === 'cleared') {
        return isDismissed;
      }
      return true;
    });
  }, [dismissedIds, filter, notifications]);

  const getNotificationBadgeState = (notification) => {
    if (dismissedIds.includes(notification._id)) {
      return 'cleared';
    }
    return notification.isRead ? 'read' : 'unread';
  };

  const isActionableNotification = (notification) => {
    const requestStatus = String(notification.data?.status || '').toLowerCase();
    return (
      notification.type === 'branch_request_submitted' &&
      Boolean(notification.data?.requestId) &&
      requestStatus !== 'approved' &&
      requestStatus !== 'rejected'
    );
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`${API_BASE_URL}/api/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((item) =>
          item._id === notificationId ? { ...item, isRead: true } : item
        )
      );
    } catch (err) {
      setError('Failed to update notification status.');
    }
  };

  const openNotification = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    if (
      notification.type === 'branch_request_submitted' &&
      notification.data?.requestId
    ) {
      try {
        const res = await api.get(
          `${API_BASE_URL}/api/insurer-branch-requests/${notification.data.requestId}`
        );
        setActiveRequest(res.data);
        setShowRejectBox(false);
        setRejectionReason('');
        setActionError('');
      } catch (err) {
        setError('Failed to load branch request details.');
      }
    }
  };

  const closeRequestModal = () => {
    setActiveRequest(null);
    setRejectionReason('');
    setShowRejectBox(false);
    setActionError('');
  };

  const submitRequestAction = async (action) => {
    if (!activeRequest?._id) return;

    if (action === 'reject' && !rejectionReason.trim()) {
      setActionError('Rejection reason is required.');
      return;
    }

    setActionLoading(true);
    setActionError('');
    try {
      if (action === 'approve') {
        await api.post(
          `${API_BASE_URL}/api/insurer-branch-requests/${activeRequest._id}/approve`
        );
      } else {
        await api.post(
          `${API_BASE_URL}/api/insurer-branch-requests/${activeRequest._id}/reject`,
          { rejectionReason: rejectionReason.trim() }
        );
      }

      const historyRes = await api.get(`${API_BASE_URL}/api/notifications/history`);
      setNotifications(historyRes.data || []);
      closeRequestModal();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Failed to update branch request.');
    } finally {
      setActionLoading(false);
    }
  };

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <div className="notification-history-container">
        <div className="sidebar">
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        </div>
        <div className={`notification-history-content ${collapsed ? 'collapsed' : ''}`}>
          <div className="notification-history-sticky-header">
            <Header />
          </div>
          <div className="notification-history-scroll-area">
            <section>
              <div className="notification-history-heading">
                <div>
                  <h2>Notification History</h2>
                  {/* <p>Review all notifications and complete pending approval actions here.</p> */}
                </div>
                <div className="notification-filter-group">
                  <button
                    type="button"
                    className={filter === 'all' ? 'active' : ''}
                    onClick={() => setFilter('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={filter === 'read' ? 'active' : ''}
                    onClick={() => setFilter('read')}
                  >
                    Read
                  </button>
                  <button
                    type="button"
                    className={filter === 'unread' ? 'active' : ''}
                    onClick={() => setFilter('unread')}
                  >
                    Unread
                  </button>
                  <button
                    type="button"
                    className={filter === 'cleared' ? 'active' : ''}
                    onClick={() => setFilter('cleared')}
                  >
                    Cleared
                  </button>
                </div>
              </div>

              {error && <p className="notification-history-error">{error}</p>}

              <div className="notification-history-table-wrap">
                {loading ? (
                  <div className="notification-history-empty">Loading notifications...</div>
                ) : visibleNotifications.length === 0 ? (
                  <div className="notification-history-empty">No notifications found for this filter.</div>
                ) : (
                  <>
                    <table className="notification-history-table notification-history-table-head">
                      <colgroup>
                        <col className="notification-col-title" />
                        <col className="notification-col-message" />
                        <col className="notification-col-type" />
                        <col className="notification-col-status" />
                        <col className="notification-col-date" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Message</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                    </table>
                    <div className="notification-history-table-body-wrap">
                      <table className="notification-history-table notification-history-table-body">
                        <colgroup>
                          <col className="notification-col-title" />
                          <col className="notification-col-message" />
                          <col className="notification-col-type" />
                          <col className="notification-col-status" />
                          <col className="notification-col-date" />
                        </colgroup>
                        <tbody>
                          {visibleNotifications.map((notification) => {
                            return (
                              <tr key={notification._id}>
                                <td>
                                  <div className="notification-title-wrap">
                                    <span
                                      className={`notification-state-dot ${getNotificationBadgeState(notification)}`}
                                      title={getNotificationBadgeState(notification)}
                                    />
                                    {isActionableNotification(notification) ? (
                                      <button
                                        type="button"
                                        className="notification-title-btn"
                                        onClick={() => openNotification(notification)}
                                      >
                                        {notification.title}
                                      </button>
                                    ) : (
                                      <span className="notification-title-text">
                                        {notification.title}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>{notification.message}</td>
                                <td>{notification.type}</td>
                                <td>{notification.data?.status || '-'}</td>
                                <td>{formatTime(notification.createdAt)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {activeRequest && (
        <div className="notification-modal-overlay">
          <div className="notification-modal">
            <div className="notification-modal-head">
              <div>
                <h3>Branch Approval Request</h3>
                <p>Review branch details before approving or rejecting.</p>
              </div>
              <button
                type="button"
                className="notification-close-btn"
                onClick={closeRequestModal}
              >
                ×
              </button>
            </div>

            <div className="notification-request-grid">
              <div>
                <span>Insurer</span>
                <strong>{activeRequest.insurerName}</strong>
              </div>
              <div>
                <span>Branch</span>
                <strong>{activeRequest.branchName}</strong>
              </div>
              <div>
                <span>Requested By</span>
                <strong>{activeRequest.requestedBy?.userName || '-'}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong className={`notification-status ${activeRequest.status}`}>
                  {activeRequest.status}
                </strong>
              </div>
              <div>
                <span>Contact Person</span>
                <strong>{activeRequest.contactPersonName}</strong>
              </div>
              <div>
                <span>Contact Number</span>
                <strong>{activeRequest.contactNumber}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{activeRequest.email}</strong>
              </div>
              <div>
                <span>Location</span>
                <strong>{[activeRequest.city, activeRequest.state].filter(Boolean).join(', ')}</strong>
              </div>
              <div className="full-width">
                <span>Address</span>
                <strong>{activeRequest.address}</strong>
              </div>
              {activeRequest.auditLogs?.length > 0 && (
                <div className="full-width notification-audit">
                  <span>Audit Log</span>
                  {activeRequest.auditLogs.map((item, index) => (
                    <p key={`${item.action}-${index}`}>
                      {item.action} by {item.actedByName || 'System'} on {formatTime(item.at)}
                      {item.note ? `: ${item.note}` : ''}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {showRejectBox && (
              <div className="notification-reject-box">
                <label>Reason for rejection</label>
                <textarea
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </div>
            )}

            {actionError && <p className="notification-error">{actionError}</p>}

            <div className="notification-modal-actions">
              {activeRequest.status === 'pending' ? (
                <>
                  <button
                    type="button"
                    className="approve-btn"
                    disabled={actionLoading}
                    onClick={() => submitRequestAction('approve')}
                  >
                    <Check size={16} /> Approve
                  </button>
                  {!showRejectBox ? (
                    <button
                      type="button"
                      className="reject-btn"
                      disabled={actionLoading}
                      onClick={() => {
                        setShowRejectBox(true);
                        setActionError('');
                      }}
                    >
                      <XCircle size={16} /> Reject
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="reject-btn"
                      disabled={actionLoading}
                      onClick={() => submitRequestAction('reject')}
                    >
                      <XCircle size={16} /> Confirm Rejection
                    </button>
                  )}
                </>
              ) : (
                <button type="button" className="close-secondary-btn" onClick={closeRequestModal}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationHistory;
